import { useState } from 'react';
import { Edit } from 'react-feather';

import { Button, Input, Modal, TeamSelector, Typography } from '@/components';
import { useAuthUser, useEffectOnce, useTeamSelect, useTeamUpdate } from '@/hooks';
import { useTranslation } from 'react-i18next';

import styles from './TeamHeader.module.css';

interface TeamHeaderProps {
  setTeams: React.Dispatch<React.SetStateAction<Api.Team[]>>;
  isLoadingTeams?: boolean;
  teams: Api.Team[];
}

export function TeamHeader({ isLoadingTeams = false, setTeams, teams = [] }: TeamHeaderProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [teamName, setTeamName] = useState('');
  const { t } = useTranslation();
  const user = useAuthUser();

  const { isLoading: isUpdatingTeam, mutate: teamUpdate } = useTeamUpdate({
    teamId: user?.team?.team_id || '',
    name: teamName,
    onSuccess: () => {
      if (typeof setTeams === 'function') {
        setTeams((prevState) => [
          ...prevState.map((team) => {
            if (team.is_current) {
              return {
                ...team,
                name: teamName
              };
            }

            return team;
          })
        ]);
      }

      setIsRenaming(false);
    }
  });

  const { isLoading: isSwitchingTeam, mutate: teamSwitch } = useTeamSelect({
    teamId: user?.team?.team_id
  });

  useEffectOnce(() => {
    if (teams.length > 0 && user.team?.name && !teamName) {
      setTeamName(user.team?.name);
    }
  }, [teams, user, teamName]);

  const handleName = (event: React.ChangeEvent<HTMLInputElement>) =>
    setTeamName(event.target?.value);

  const handleTeamSwitch = (selectedTeamId: string) => {
    teamSwitch(selectedTeamId);
  };

  const handleTeamUpdate = () => teamUpdate();

  return (
    <>
      <div className={styles.teamHeaderSwitch}>
        {teams.length > 1 && (
          <TeamSelector
            teams={teams}
            currentTeamId={user?.team?.team_id}
            currentTeamName={user?.team?.name}
            onChange={handleTeamSwitch}
            disabled={isLoadingTeams}
            isLoading={isSwitchingTeam}
          />
        )}
      </div>

      <div>
        <Typography sizes={['h3']}>{user.team?.name}</Typography>

        {user.permissions?.can_rename_team && teamName && (
          <button
            onClick={() => setIsRenaming((prevState) => !prevState)}
            disabled={isLoadingTeams || isSwitchingTeam}
            className={styles.teamHeaderCtaRename}
            type="button"
          >
            <Edit size={20} />
          </button>
        )}
      </div>

      {isRenaming && (
        <Modal handleClose={() => setIsRenaming(false)}>
          <div className={styles.teamHeaderModal}>
            <Input
              label={t('teamHeader.modalRename')}
              disabled={isUpdatingTeam}
              onChange={handleName}
              value={teamName}
              name="name"
              required
            />

            <Button onClick={handleTeamUpdate} isLoading={isUpdatingTeam} theme="cta">
              {t('teamHeader.modalRenameSubmit')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
